import React from 'react';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Chip } from '@material-ui/core';
import { format } from 'date-fns';
import Countdown from 'react-countdown';
import pluralize from 'pluralize';

import { formatPrice } from 'utils/helpers';
import { getRequest } from 'utils/api';
import { openModal } from 'features/utilsSlice';
import useIsAgency from 'hooks/useIsAgency';
import NoAvatarImage from 'public/images/no-avatar.svg';
import ChevronRight from 'public/icons/chevron-right-grey.svg';
import styles from 'styles/components/ToDos.module.scss';

const avatarFullSize = { width: '100%', height: '100%' };

const ToDo = ({ todo }) => {
  const dispatch = useDispatch();
  const networks = useSelector(state => state.utils.networks);
  const currentUser = useSelector(state => state.auth.currentUser);
  const isFullAgency = currentUser?.full_management_agency;
  const overdue =
    new Date() > new Date(todo.latest_start_time) && !todo.posted_at && todo.status !== 'approved';
  const networkIconUrl = networks.find(network => network.id === todo.network_id)?.active_icon_url;
  const isAgency = useIsAgency();

  const getIndicatorColor = () => {
    const { status } = todo;

    if (status === 'approved') {
      return '#12dfc7';
    } else if (status === 'rejected') {
      return '#ff4b4b';
    } else if (status === 'pending' && !todo.posted_at) {
      return '#12dfc7';
    } else if (todo.posted_at && new Date() >= todo.expires_at) {
      return '#5b00db';
    } else if (new Date() >= todo.expires_at) {
      return '#ff52ca';
    }
  };

  const getToDoTitle = () => {
    const { status } = todo;

    if (status === 'approved') {
      return 'ToDo Approved';
    } else if (status === 'rejected') {
      return `${todo.advertiser_name} has rejected your post`;
    } else if (status === 'awaiting_approval') {
      return 'Awaiting approval';
    } else if (
      status === 'pending' &&
      !todo.posted_at &&
      new Date() >= new Date(todo.earliest_start_time)
    ) {
      return 'Time to post';
    } else if (todo.posted_at && new Date() <= new Date(todo.expires_at)) {
      return (
        <Countdown
          date={new Date(todo.expires_at)}
          intervalDelay={60000}
          renderer={({ days, hours, minutes }) => {
            if (days >= 2 && hours >= 0) {
              return `Take down in ${days} days ${hours > 0 &&
                `${hours} ${pluralize('hours', hours)}`} `;
            } else if (hours > 0 && days < 2) {
              const dayToHours = days * 24;
              return `Take down in ${hours + dayToHours} ${pluralize('hours', hours)}`;
            } else if (minutes > 0 && hours === 0) {
              return `Take down in ${minutes} ${pluralize('minutes', minutes)}`;
            } else {
              return '0 minutes';
            }
          }}
        />
      );
    } else if (todo.posted_at && new Date() >= new Date(todo.expires_at)) {
      return todo.guaranteed_gains
        ? "You can now track your analytics on the buyer's OnlyFans campaign."
        : 'Time to take down the post and upload delivery screenshot';
    }
  };

  const getDescription = () => {
    if (todo.status === 'approved') {
      return `Approved: ${format(new Date(todo.approved_at), 'MM/dd/yy')}`;
    } else if (todo.status === 'rejected' && todo.rejection_reason) {
      return todo.rejection_reason;
    } else {
      return `Order #${todo.gig_id} for ${todo.advertiser_name}`;
    }
  };

  const handleOpenToDo = () => {
    Promise.all([
      getRequest({
        endpoint: `gigs/${todo.gig_id}`,
      }),
      getRequest({
        endpoint: `milestones/${todo.id}`,
      }),
    ])
      .then(([gig, milestone]) => {
        dispatch(openModal({ name: 'New Milestone', data: { gig, milestone } }));
      })
      .catch(() => {});
  };

  return (
    <tr
      className={styles.card}
      onClick={handleOpenToDo}
      style={isAgency ? { alignItems: 'center' } : {}}
    >
      <td className={styles.column} style={{ minWidth: 300, flex: 3 }}>
        <div className={styles.indicator} style={{ backgroundColor: getIndicatorColor() }} />

        <div style={{ paddingLeft: 10, marginRight: 'auto' }}>
          <h3>{getToDoTitle()}</h3>
          <p>{getDescription()}</p>
        </div>
      </td>

      <td className={styles.column} style={{ minWidth: 150, flex: 1 }}>
        <h4>{formatPrice(todo.amount)}</h4>
      </td>
      <td className={styles.column} style={{ minWidth: 200, flex: 2 }}>
        {networkIconUrl && (
          <Image unoptimized src={networkIconUrl} alt="Network" width={30} height={30} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ fontSize: 12, color: '#323643', marginLeft: 7 }}>
            {todo.name?.replace('Image & Likeness', '')}
          </span>

          {todo.guaranteed_gains && isFullAgency && (
            <Chip
              label={`${todo.guaranteed_gains_current || 0} of ${todo.guaranteed_gains_max ||
                0} subs`}
              size="medium"
              className="light-purple-chip"
              style={{
                marginTop: 4,
                marginLeft: 7,
                width: 'fit-content',
              }}
            />
          )}
        </div>
      </td>

      <td className={styles.column} style={{ minWidth: 200, flex: 1 }}>
        <Avatar src={todo.avatar_url} alt="Profile" className={styles.avatar}>
          <NoAvatarImage style={avatarFullSize} />
        </Avatar>

        <h4 style={{ marginLeft: 7 }}>{todo.display_name}</h4>
      </td>

      <td
        className={styles.column}
        style={{ minWidth: 200, flex: 2, justifyContent: 'space-between' }}
      >
        <span style={{ minWidth: 100, fontSize: 12, color: '#323643', letterSpacing: '-0.06px' }}>
          {todo.username}
        </span>

        {overdue && (
          <Chip
            label={todo.guaranteed_gains ? 'In Progress' : 'Overdue'}
            color="secondary"
            className={styles.chip}
          />
        )}

        <ChevronRight />
      </td>
    </tr>
  );
};

export default ToDo;
